/**
 * Chart pattern detection — pure functions on OHLCV bar arrays.
 *
 * Keeps NO dependency on klinecharts, React, or the DOM: it consumes `EdgeBar[]`
 * and reuses `seriesMath` helpers only. That purity is deliberate — it makes the
 * logic cheap to unit-test and is the seam a future backend (Python) engine slots
 * into behind the same `Pattern` contract. Rendering lives in `patternOverlay.ts`.
 *
 * See docs/superpowers/specs/2026-07-16-chart-pattern-recognition-design.md.
 */

import type { EdgeBar } from '../types/chart'
import { trueRangeSeries, wilderSeries } from './seriesMath'

/**
 * Which price a swing or a touch is measured on: the candle's WICK (high /
 * low) or its BODY (the higher / lower of open and close). One dial shared by
 * every auto-analysis detector (S/R, auto Fib, trendlines) so a body line is
 * measured against bodies and a wick line against wicks. ATR always stays on
 * the raw wicks, so thresholds and tolerances mean the same thing in both
 * modes; only the series being fitted changes.
 */
export type TouchInput = 'wicks' | 'bodies'

/** High / low accessors for a `TouchInput`, chosen once per call. */
export function touchAccessors(input: TouchInput): {
  hi: (b: EdgeBar) => number
  lo: (b: EdgeBar) => number
} {
  return input === 'bodies'
    ? { hi: (b) => Math.max(b.open, b.close), lo: (b) => Math.min(b.open, b.close) }
    : { hi: (b) => b.high, lo: (b) => b.low }
}

export interface Swing {
  index: number
  timestamp: number
  price: number
  kind: 'high' | 'low'
  magnitude: number // move size vs previous swing, in ATR units
}

/* ------------------------------------------------------------------ */
/*  Pattern contract — FROZEN. The future Python engine emits this      */
/*  identically (design §5). Keep TS and Python shapes in lock-step.    */
/* ------------------------------------------------------------------ */

export interface PatternAnchor {
  timestamp: number
  price: number
  role: string // 'touch' | 'left_shoulder' | 'head' | 'neckline' | 'pivot' ...
}

export interface PatternLine {
  from: [number, number] // [timestamp, price]
  to: [number, number]
  kind: string // 'support' | 'resistance' | 'neckline' | 'channel' ...
}

export interface Pattern {
  id: string
  type: string // 'support' | 'resistance' | 'trendline' | 'head_shoulders' ...
  symbol: string
  timeframe: string
  anchors: PatternAnchor[]
  lines: PatternLine[]
  direction: 'bullish' | 'bearish' | 'neutral'
  status: 'forming' | 'confirmed' | 'failed'
  pivotPrice: number | null
  confidence: number // 0..1 aggregate quality score (design §8.1)
  scoreComponents: Record<string, number> // sub-scores kept for explainability
  detectedAt: number
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
const mean = (xs: readonly number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0)
function median(xs: readonly number[]): number {
  if (!xs.length) return 0
  const s = [...xs].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/**
 * Reduce OHLCV bars to alternating swing highs/lows via an ATR-scaled ZigZag.
 *
 * A running extreme is confirmed as a swing once price reverses away from it by
 * more than `thresholdMult * ATR` (ATR measured at the extreme's bar). Scaling by
 * ATR makes the same params work on a $2 and a $2000 stock, and on quiet vs.
 * volatile names — it is the small-cap/large-cap normalization. Running high and
 * low are tracked with independent indices (they must not clobber each other).
 * The final, still-unconfirmed leg is appended as a provisional swing so the
 * closing pivot is not dropped.
 */
export function detectSwings(
  input: readonly EdgeBar[],
  atrPeriod = 14,
  thresholdMult = 3,
  touchInput: TouchInput = 'wicks',
): Swing[] {
  const n = input.length
  if (n === 0) return []
  const { hi, lo } = touchAccessors(touchInput)

  const atr = wilderSeries(trueRangeSeries(input), atrPeriod) // (number | undefined)[]
  const swings: Swing[] = []

  const emit = (idx: number, price: number, kind: 'high' | 'low') => {
    const prev = swings.length ? swings[swings.length - 1].price : price
    const a = atr[idx]
    swings.push({
      index: idx,
      timestamp: input[idx].timestamp,
      price,
      kind,
      magnitude: a != null && a > 0 ? Math.abs(price - prev) / a : 0,
    })
  }

  let hiIdx = 0
  let hiVal = hi(input[0])
  let loIdx = 0
  let loVal = lo(input[0])
  let direction: 'up' | 'down' | null = null

  for (let i = 1; i < n; i++) {
    if (direction !== 'down' && hi(input[i]) >= hiVal) {
      hiVal = hi(input[i])
      hiIdx = i
    }
    if (direction !== 'up' && lo(input[i]) <= loVal) {
      loVal = lo(input[i])
      loIdx = i
    }

    // Threshold uses the CURRENT bar's ATR (the reversal is detected here). The
    // pivot's own bar may sit in the ATR warm-up region (undefined), so keying the
    // threshold off `atr[i]` — not `atr[pivotIdx]` — is both warm and correct.
    const thr = atr[i]
    if (thr == null) continue

    if (direction === null || direction === 'up') {
      if (hiVal - lo(input[i]) > thresholdMult * thr) {
        emit(hiIdx, hiVal, 'high')
        direction = 'down'
        loVal = lo(input[i])
        loIdx = i
      }
    }
    if (direction === null || direction === 'down') {
      if (hi(input[i]) - loVal > thresholdMult * thr) {
        emit(loIdx, loVal, 'low')
        direction = 'up'
        hiVal = hi(input[i])
        hiIdx = i
      }
    }
  }

  // Append the closing (unconfirmed) leg so the most recent pivot survives.
  if (direction === 'up') emit(hiIdx, hiVal, 'high')
  else if (direction === 'down') emit(loIdx, loVal, 'low')

  return swings
}

/* ------------------------------------------------------------------ */
/*  Phase 1 — Support / Resistance                                      */
/* ------------------------------------------------------------------ */

export interface SROptions {
  atrPeriod?: number
  /** Half-window for local-extrema touch detection (a bar is a local high/low if
   *  it is the max/min over ±pivotWindow bars). */
  pivotWindow?: number
  /** Cluster tolerance in ATR units — touches within this band form one zone. */
  clusterMult?: number
  /** Minimum touches for a price band to count as a level (unless volume-significant). */
  minTouches?: number
  /** A single max/min qualifies as a level (no retest needed) if its volume is at
   *  least this multiple of average — a blowoff high / capitulation low. */
  significantVolMult?: number
  /** Reject a level once price has decisively closed through it more than this many
   *  times — it's no longer respected (design §8.10). */
  maxCrossings?: number
  /** Wicks or candle bodies as the touch series. Default 'wicks'. */
  touchInput?: TouchInput
}

/**
 * Local extrema of bar highs (`kind: 'high'`) or lows (`kind: 'low'`): bars that
 * are the max/min over ±`window` neighbours. Unlike the ATR-ZigZag (which
 * collapses a whole consolidation into ONE pivot), this captures EVERY reach-up /
 * reach-down — so a price that is repeatedly tested registers as multiple touches.
 * That is what horizontal S/R needs (see the AAPL June case, design §8.6).
 */
function localExtrema(
  bars: readonly EdgeBar[],
  kind: 'high' | 'low',
  window: number,
  touchInput: TouchInput = 'wicks',
): Array<{ index: number; price: number }> {
  const out: Array<{ index: number; price: number }> = []
  const { hi, lo } = touchAccessors(touchInput)
  for (let i = 0; i < bars.length; i++) {
    const v = kind === 'high' ? hi(bars[i]) : lo(bars[i])
    let extreme = true
    for (let j = Math.max(0, i - window); j <= Math.min(bars.length - 1, i + window); j++) {
      if (j === i) continue
      const w = kind === 'high' ? hi(bars[j]) : lo(bars[j])
      if ((kind === 'high' && w > v) || (kind === 'low' && w < v)) {
        extreme = false
        break
      }
    }
    if (extreme) out.push({ index: i, price: v })
  }
  return out
}

/**
 * Number of times price DECISIVELY closes through a level — crossing from one
 * side to the other, with a ±0.5·ATR deadband so noise-at-the-level and WICK
 * pokes (which close back) don't count. This is the core relevance signal for
 * S/R (design §8.10): a level price crosses repeatedly is NOT support/resistance —
 * price passes through it freely.
 *
 * - 0 crossings → unbroken (a clean blow-off top / untested level) — strongest.
 * - 1 crossing  → a single clean break, i.e. a role reversal — still valid.
 * - many        → whipsaw noise — reject.
 *
 * Counting crossings (not one-sided breaks) is what distinguishes "broke once and
 * flipped role" from "doesn't respond to it" — and it makes the metric role-safe,
 * unlike the old span-based `respect`.
 */
export function levelCrossings(bars: readonly EdgeBar[], price: number, atr: number): number {
  if (atr <= 0) return 0
  const band = 0.5 * atr
  let crossings = 0
  let side = 0 // -1 below, +1 above, 0 within the deadband
  for (const b of bars) {
    let s = 0
    if (b.close > price + band) s = 1
    else if (b.close < price - band) s = -1
    else continue // inside the deadband — not decisively on either side
    if (side !== 0 && s !== side) crossings++
    side = s
  }
  return crossings
}

/**
 * Detect horizontal support/resistance zones from **touch density** — clustering
 * local bar-high/low extrema into ATR-scaled price bands. Using local extrema
 * (not ATR-ZigZag swings) is deliberate: a consolidation that repeatedly tests one
 * price must register as MANY touches, but ZigZag collapses it to a single pivot,
 * so a swing-based S/R misses exactly the levels a trader cares about (design §8.6,
 * validated against AAPL's June 2026 ~$316 level). Confidence blends touch count,
 * recency, respect, and proximity (design §8.1/§8.5).
 */
export function detectSupportResistance(
  bars: readonly EdgeBar[],
  symbol: string,
  timeframe: string,
  opts: SROptions = {},
): Pattern[] {
  const {
    atrPeriod = 14,
    pivotWindow = 2,
    clusterMult = 1.0,
    minTouches = 2,
    significantVolMult = 5,
    maxCrossings = 5,
    touchInput = 'wicks',
  } = opts
  const n = bars.length
  if (n === 0) return []

  const atrVals = wilderSeries(trueRangeSeries(bars), atrPeriod).filter(
    (a): a is number => a != null && a > 0,
  )
  const lastBar = bars[n - 1]
  const medAtr = atrVals.length ? median(atrVals) : lastBar.close * 0.01
  const tol = clusterMult * medAtr
  const avgVol = Math.max(1, mean(bars.map((b) => b.volume)))

  // Touch points = local highs (reach-ups / rejections) + local lows (reach-downs
  // / holds). Every test of a level counts, across all of time.
  const touchPts = [
    ...localExtrema(bars, 'high', pivotWindow, touchInput),
    ...localExtrema(bars, 'low', pivotWindow, touchInput),
  ].sort((a, b) => a.price - b.price)
  if (touchPts.length < minTouches) return []

  // Greedy 1-D clustering of touch prices, BOUNDED WIDTH: a touch joins the
  // current cluster only if within `tol` of the cluster's first (lowest) price.
  // Comparing to the cluster START — not the previous point — prevents transitive
  // chaining, which on a dense low-priced series would otherwise merge the whole
  // range into one blob (MBOT: 153 touches in one cluster).
  const clusters: Array<Array<{ index: number; price: number }>> = []
  for (const p of touchPts) {
    const last = clusters[clusters.length - 1]
    if (last && p.price - last[0].price <= tol) last.push(p)
    else clusters.push([p])
  }

  const patterns: Pattern[] = []
  for (const cluster of clusters) {
    const touches = cluster.length
    // Volume significance (design §8.7): peak relative volume among the cluster's
    // touches — 1× avg → 0, ≥5× → 1.
    const maxRelVol = Math.max(...cluster.map((c) => bars[c.index].volume)) / avgVol
    const volumeScore = clamp((maxRelVol - 1) / 4, 0, 1)
    // An S/R level qualifies with enough touches OR as a SINGLE significant
    // max/min on large volume — a blowoff high / capitulation low is a level even
    // without a retest (user, 2026-07-16, §8.9). The multi-touch requirement is for
    // TREND LINES (Phase 2), which need ≥2 points to define a line — not for S/R.
    if (touches < minTouches && maxRelVol < significantVolMult) continue

    // Median so the drawn line sits ON a real touch, not a drifting midpoint.
    const price = median(cluster.map((c) => c.price))
    const type = price < lastBar.close ? 'support' : 'resistance'
    const idxs = cluster.map((c) => c.index)
    const lastTouchIdx = Math.max(...idxs)
    const firstTs = bars[Math.min(...idxs)].timestamp

    // RELEVANCE FILTER (design §8.10): a level price has closed through many times
    // is not S/R — reject it. Crossings are role-safe (1 = a clean role reversal,
    // many = whipsaw noise).
    const crossings = levelCrossings(bars, price, medAtr)
    if (crossings > maxCrossings) continue

    // Sub-scores (design §8.1) — kept for explainability.
    const touchScore = clamp((touches - 1) / 3, 0, 1) // 2→0.33, 3→0.67, 4+→1.0
    const recency = clamp(lastTouchIdx / Math.max(1, n - 1), 0, 1)
    // Respect = how UNBROKEN the level is, from crossing count (§8.10). Heavy weight,
    // symmetric for support/resistance (crossings are role-safe). 0 crossings → 1.
    const respect = clamp(1 - crossings / 4, 0, 1)
    // Proximity / actionability (design §8.2): far levels fade — BUT volume rescues
    // them. A big-volume spike zone (MBOT's 45M $4.50) stays relevant at any
    // distance because `volumeScore` lifts the relevance floor; a low-volume far
    // level still fades out. Volume is what separates a real far level from junk.
    const distFrac = Math.abs(price - lastBar.close) / Math.max(1e-9, Math.abs(lastBar.close))
    const proximity = clamp(1 - distFrac / 0.4, 0, 1)
    // Two signals lead: VOLUME (a big-volume level is a major zone) and RESPECT (an
    // unbroken level is real S/R; one crossed repeatedly is noise, already rejected).
    const quality = clamp(
      0.2 * touchScore + 0.15 * recency + 0.35 * respect + 0.3 * volumeScore,
      0,
      1,
    )
    // Volume also lifts the relevance floor so a big-volume level survives distance.
    const relevance = clamp(0.5 + 0.5 * proximity + 0.4 * volumeScore, 0, 1)
    const confidence = clamp(quality * relevance, 0, 1)

    patterns.push({
      id: `${type}-${Math.round(price * 100)}`,
      type,
      symbol,
      timeframe,
      anchors: cluster.map((c) => ({
        timestamp: bars[c.index].timestamp,
        price: c.price,
        role: 'touch',
      })),
      lines: [{ from: [firstTs, price], to: [lastBar.timestamp, price], kind: type }],
      direction: type === 'support' ? 'bullish' : 'bearish',
      status: 'confirmed',
      pivotPrice: null,
      confidence,
      scoreComponents: {
        touches: touchScore,
        recency,
        respect,
        volume: volumeScore,
        proximity,
        touchCount: touches,
        crossings,
        // Half-height of the zone band the overlay shades around the level: the
        // cluster's price spread, floored so a tight cluster still reads as a
        // zone rather than a hairline. Price units, so the renderer can map it.
        bandHalf: Math.max(
          (Math.max(...cluster.map((c) => c.price)) - Math.min(...cluster.map((c) => c.price))) / 2,
          0.15 * medAtr,
        ),
      },
      detectedAt: lastBar.timestamp,
    })
  }
  return patterns
}

/* ------------------------------------------------------------------ */
/*  Phase 1 — Volume dry-up (pattern-independent, design §8.4)          */
/* ------------------------------------------------------------------ */

/**
 * Ratio of recent average volume to base average volume. < ~0.6 signals a
 * volume dry-up (supply exhaustion — the O'Neil base/handle tell). Always
 * computable, independent of any pattern.
 */
export function volumeDryupRatio(bars: readonly EdgeBar[], recent = 5, base = 50): number {
  if (bars.length === 0) return 1
  const vols = bars.map((b) => b.volume)
  const recentAvg = mean(vols.slice(-Math.min(recent, vols.length)))
  const baseAvg = mean(vols.slice(-Math.min(base, vols.length)))
  return baseAvg > 0 ? recentAvg / baseAvg : 1
}

/* ------------------------------------------------------------------ */
/*  Orchestrator + curation                                             */
/* ------------------------------------------------------------------ */

/**
 * Single entry point the chart calls (and the seam a Python engine replaces).
 * v1 detects support/resistance; Phase 2/3 extend this with trend lines and
 * named patterns. Over-generates candidates — call `curatePatterns` to select.
 */
export function detectPatterns(
  bars: readonly EdgeBar[],
  symbol: string,
  timeframe: string,
  opts: SROptions = {},
): Pattern[] {
  return detectSupportResistance(bars, symbol, timeframe, opts)
}

export interface CurateOptions {
  /** Max patterns to draw (per group). */
  maxN?: number
  /** Drop patterns below this confidence (the sensitivity slider). */
  minConfidence?: number
  /** Overlap band as a fraction of price — zones within collapse to the best. */
  priceTolerance?: number
  /** Reserve up to this many slots for the highest-volume levels, so a major
   *  supply/demand zone isn't crowded out by a cluster of near-price levels. */
  reserveSignificant?: number
}

/** Representative price of a pattern (mean of anchor prices). */
function representativePrice(p: Pattern): number {
  if (p.anchors.length) return mean(p.anchors.map((a) => a.price))
  return p.lines[0]?.from[1] ?? 0
}

/**
 * Display curation (design §8.2) — the "don't show too much" layer, kept
 * separate from detection. Drops sub-threshold patterns, suppresses overlapping
 * ones (non-maximum suppression by price region, highest confidence wins), caps
 * at `maxN`, and RESERVES slots for the highest-volume levels so a major spike
 * zone (e.g. MBOT's far $4.50) survives a crowd of near-price levels (§8.7).
 */
export function curatePatterns(patterns: readonly Pattern[], opts: CurateOptions = {}): Pattern[] {
  const { maxN = 5, minConfidence = 0, priceTolerance = 0.01, reserveSignificant = 2 } = opts
  const eligible = patterns
    .filter((p) => p.confidence >= minConfidence)
    .sort((a, b) => b.confidence - a.confidence)

  const kept: Pattern[] = []
  const overlaps = (p: Pattern) => {
    const pp = representativePrice(p)
    return kept.some((k) => {
      const kp = representativePrice(k)
      return Math.abs(pp - kp) / Math.max(1e-9, Math.abs(kp)) <= priceTolerance
    })
  }
  const tryKeep = (p: Pattern) => {
    if (kept.length >= maxN || kept.includes(p) || overlaps(p)) return
    kept.push(p)
  }

  // Reserve slots for the most significant (highest-volume) levels FIRST, so they
  // aren't crowded out of the top-N by a cluster of higher-confidence near levels.
  if (reserveSignificant > 0) {
    eligible
      .filter((p) => (p.scoreComponents?.volume ?? 0) >= 0.9)
      .sort((a, b) => (b.scoreComponents.volume ?? 0) - (a.scoreComponents.volume ?? 0))
      .slice(0, reserveSignificant)
      .forEach(tryKeep)
  }
  // Fill the rest by confidence.
  for (const p of eligible) tryKeep(p)

  return kept.sort((a, b) => b.confidence - a.confidence)
}
